/**
 * LastfmBadge — small "Powered by Last.fm" pill using Last.fm's
 * official brand red (#D51007). Used to indicate which data sections
 * are sourced from Last.fm. Variants:
 *
 *   - "inline"  : tiny mono caption + red "last.fm" pill (footer use)
 *   - "prominent": larger badge with their wordmark + radio glyph (top-of-modal)
 *
 * Optionally renders as a link that opens a Last.fm URL in a new tab.
 */
import { Radio } from 'lucide-react';

const LASTFM_RED = '#D51007';

function LastfmWordmark({ className = '' }) {
  // Small inline SVG approximating Last.fm's red rounded-rect "last.fm" wordmark.
  // Kept as inline SVG (not raster) so it scales crisply and stays a single asset.
  return (
    <span
      className={`inline-flex items-center justify-center rounded-[4px] px-1.5 py-[2px] font-sans font-bold text-white leading-none tracking-tight ${className}`}
      style={{ background: LASTFM_RED, fontSize: '0.65rem' }}
      aria-label="Last.fm"
    >
      last.fm
    </span>
  );
}

export default function LastfmBadge({
  variant = 'inline',
  href,
  className = '',
}) {
  const Tag = href ? 'a' : 'span';
  const linkProps = href
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  if (variant === 'prominent') {
    return (
      <Tag
        {...linkProps}
        className={`inline-flex items-center gap-2 rounded-full border border-border bg-background/80 backdrop-blur px-2.5 py-1 text-[11px] font-mono text-muted-foreground hover:text-foreground transition-colors ${className}`}
        data-testid="lastfm-badge-prominent"
      >
        <Radio className="w-3 h-3" style={{ color: LASTFM_RED }} />
        <span>Powered by</span>
        <LastfmWordmark />
      </Tag>
    );
  }

  // inline variant — for footer attribution
  return (
    <Tag
      {...linkProps}
      className={`inline-flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground/80 hover:text-foreground transition-colors ${className}`}
      data-testid="lastfm-badge-inline"
    >
      <span>Powered by</span>
      <LastfmWordmark />
    </Tag>
  );
}
